import { useLayoutEffect, useRef, type ReactNode } from "react";
import { Suspense } from "react";
import { Group, InstancedMesh, Object3D } from "three";
import { Clone } from "@react-three/drei";
import { useOptionalGLTF } from "./materials";
import { BRAND_GREEN } from "./layout";

const MODELS = {
  recepcion: "/assets/models/recepcion.glb",
  cubiculo: "/assets/models/cubiculo.glb",
  sillas: "/assets/models/sillas.glb",
  cono: "/assets/models/cono.glb",
  camara: "/assets/models/camara.glb",
} as const;

export type FurnitureKind = keyof typeof MODELS;

/** Dimensión real de referencia por modelo (metros) para normalizeScale. */
const MODEL_DIMS: Record<FurnitureKind, { target: number; axis: "x" | "y" | "z" | "max" }> = {
  recepcion: { target: 2.4, axis: "x" }, // mostrador 2.4 m de ancho
  cubiculo: { target: 1.4, axis: "x" }, // cubículo 1.4 m de ancho
  sillas: { target: 1.8, axis: "x" }, // banca 1.8 m de ancho
  cono: { target: 0.9, axis: "y" }, // cono 0.9 m de alto [NORMATIVO Anexo 60 Nota 12]
  camara: { target: 0.15, axis: "max" }, // domo real: 0.12–0.18 m de diámetro
};

interface SlotProps {
  kind: FurnitureKind;
  position?: [number, number, number];
  rotationY?: number;
  scale?: number;
  children: ReactNode;
}

/**
 * Slot de mobiliario: intenta cargar el GLB de /public/assets/models
 * (normalizado a su dimensión real por bbox); si el archivo no existe,
 * instancia el placeholder procedural recibido como children.
 */
export function FurnitureSlot({ kind, position = [0, 0, 0], rotationY = 0, scale = 1, children }: SlotProps) {
  const { scene } = useOptionalGLTF(MODELS[kind], MODEL_DIMS[kind]);
  return (
    <group position={position} rotation={[0, rotationY, 0]} scale={scale}>
      <Suspense fallback={children}>{scene ? <Clone object={scene as Group} castShadow receiveShadow /> : children}</Suspense>
    </group>
  );
}

/* ------------------------- Placeholders procedurales ------------------------- */

/** Mostrador de recepción: mesón en L con faldón y sobre. */
export function ReceptionDeskPlaceholder() {
  return (
    <group>
      <mesh position={[0, 0.55, 0]} castShadow receiveShadow>
        <boxGeometry args={[3, 1.1, 0.7]} />
        <meshStandardMaterial color="#b8b2a4" roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.12, 0]} castShadow>
        <boxGeometry args={[3.2, 0.05, 0.85]} />
        <meshStandardMaterial color="#8d8676" roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.62, 0.36]}>
        <boxGeometry args={[3, 0.5, 0.02]} />
        <meshStandardMaterial color={BRAND_GREEN} roughness={0.5} />
      </mesh>
    </group>
  );
}

/** Cubículo de verificación/examen: mesa, tabique lateral, silla y equipo. */
export function CubiclePlaceholder() {
  return (
    <group>
      <mesh position={[0, 0.74, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.4, 0.04, 0.7]} />
        <meshStandardMaterial color="#c9c3b4" roughness={0.6} />
      </mesh>
      {[-0.65, 0.65].map((x) => (
        <mesh key={x} position={[x, 0.37, 0]} castShadow>
          <boxGeometry args={[0.05, 0.72, 0.65]} />
          <meshStandardMaterial color="#a8a294" roughness={0.7} />
        </mesh>
      ))}
      {/* tabique divisorio */}
      <mesh position={[-0.7, 1.05, 0]} castShadow>
        <boxGeometry args={[0.04, 1.4, 0.9]} />
        <meshStandardMaterial color="#d3cec0" roughness={0.85} />
      </mesh>
      {/* equipo (pantalla) */}
      <mesh position={[0, 1.06, -0.18]} castShadow>
        <boxGeometry args={[0.55, 0.34, 0.03]} />
        <meshStandardMaterial color="#20262a" roughness={0.3} metalness={0.4} />
      </mesh>
      <mesh position={[0, 0.83, -0.18]}>
        <boxGeometry args={[0.06, 0.18, 0.06]} />
        <meshStandardMaterial color="#3a4145" roughness={0.5} />
      </mesh>
      <ChairPlaceholder position={[0, 0, 0.75]} />
    </group>
  );
}

/** Silla institucional sencilla. */
export function ChairPlaceholder({ position = [0, 0, 0] as [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.45, 0]} castShadow>
        <boxGeometry args={[0.45, 0.05, 0.45]} />
        <meshStandardMaterial color="#3f4a4d" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0.7, -0.21]} castShadow>
        <boxGeometry args={[0.45, 0.5, 0.05]} />
        <meshStandardMaterial color="#3f4a4d" roughness={0.6} />
      </mesh>
      {[
        [-0.19, -0.19],
        [0.19, -0.19],
        [-0.19, 0.19],
        [0.19, 0.19],
      ].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.225, z]}>
          <cylinderGeometry args={[0.02, 0.02, 0.45, 6]} />
          <meshStandardMaterial color="#7d8488" roughness={0.4} metalness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

/** Fila de sillas de espera (banca de 4 puestos). */
export function WaitingBenchPlaceholder() {
  return (
    <group>
      {[-1.2, -0.4, 0.4, 1.2].map((x) => (
        <ChairPlaceholder key={x} position={[x, 0, 0]} />
      ))}
    </group>
  );
}

/**
 * Cono de 90 cm con banda reflectiva [NORMATIVO Anexo 60 Nota 12].
 */
export function ConeGeometryParts() {
  return (
    <group>
      <mesh position={[0, 0.02, 0]}>
        <cylinderGeometry args={[0.28, 0.3, 0.04, 12]} />
        <meshStandardMaterial color="#c44e10" roughness={0.7} />
      </mesh>
      <mesh position={[0, 0.47, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.22, 0.9, 12]} />
        <meshStandardMaterial color="#e05a10" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0.55, 0]}>
        <cylinderGeometry args={[0.085, 0.115, 0.18, 12]} />
        <meshStandardMaterial color="#f2f2ee" roughness={0.25} emissive="#555548" emissiveIntensity={0.25} />
      </mesh>
    </group>
  );
}

/**
 * Conos instanciados (altura nominal 0.9 m [NORMATIVO Anexo 60 Nota 12]):
 * cuerpo (centro a y=0.45), base (y=0.025) y banda reflectiva (y=0.55),
 * tres draw calls para todos los conos de una pista.
 */
export function InstancedCones({ points }: { points: readonly (readonly [number, number])[] }) {
  const bodyRef = useRef<InstancedMesh>(null);
  const baseRef = useRef<InstancedMesh>(null);
  const bandRef = useRef<InstancedMesh>(null);
  const count = points.length;

  useLayoutEffect(() => {
    const dummy = new Object3D();
    const set = (mesh: InstancedMesh | null, y: number) => {
      if (!mesh) return;
      points.forEach(([x, z], i) => {
        dummy.position.set(x, y, z);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
    };
    set(bodyRef.current, 0.45);
    set(baseRef.current, 0.025);
    set(bandRef.current, 0.55);
  }, [points]);

  return (
    <group>
      <instancedMesh ref={bodyRef} args={[undefined, undefined, count]} castShadow>
        <cylinderGeometry args={[0.02, 0.24, 0.9, 10]} />
        <meshStandardMaterial color="#e05a10" roughness={0.6} />
      </instancedMesh>
      <instancedMesh ref={baseRef} args={[undefined, undefined, count]}>
        <cylinderGeometry args={[0.3, 0.32, 0.05, 10]} />
        <meshStandardMaterial color="#b34a0e" roughness={0.7} />
      </instancedMesh>
      <instancedMesh ref={bandRef} args={[undefined, undefined, count]}>
        <cylinderGeometry args={[0.085, 0.115, 0.18, 10]} />
        <meshStandardMaterial color="#f2f2ee" roughness={0.25} emissive="#555548" emissiveIntensity={0.25} />
      </instancedMesh>
    </group>
  );
}

/** Cámara domo de techo (representación escénica, sin captura real). */
export function DomeCameraPlaceholder({ position = [0, 0, 0] as [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, -0.03, 0]}>
        <cylinderGeometry args={[0.12, 0.14, 0.05, 16]} />
        <meshStandardMaterial color="#e8e8e4" roughness={0.4} />
      </mesh>
      <mesh position={[0, -0.1, 0]}>
        <sphereGeometry args={[0.09, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#1c2226" roughness={0.15} metalness={0.3} />
      </mesh>
    </group>
  );
}

/** Puesto de cubículo con slot GLB (cae al placeholder si falta el modelo). */
export function CubicleUnit({
  position = [0, 0, 0] as [number, number, number],
  rotationY = 0,
  lit = false,
  screenTexture = null,
}: {
  position?: [number, number, number];
  rotationY?: number;
  /** true: monitor con pantalla encendida (contenido abstracto no legible). */
  lit?: boolean;
  screenTexture?: import("three").Texture | null;
}) {
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <FurnitureSlot kind="cubiculo">
        <CubiclePlaceholder />
      </FurnitureSlot>
      {lit && screenTexture && (
        <mesh position={[0, 1.06, -0.165]}>
          <planeGeometry args={[0.5, 0.3]} />
          <meshBasicMaterial map={screenTexture} toneMapped={false} />
        </mesh>
      )}
    </group>
  );
}
